import _                                                             from 'underscore';
import React, { FC, PropsWithChildren, useEffect, useRef, useState } from 'react';
import { Form }                                                      from 'semantic-ui-react';
import { Formik, FormikConfig, FormikProps }                         from 'formik';
import * as Yup                                                      from 'yup';
import { useQuery }                                                  from '@apollo/react-hooks';

import { CorvalProjectActivity, SystemEvent, Uuid } from '@src/types';

import { useProjectActivities, useSystemModals, useSystemToasts, useYupExtensions } from '@src/hooks';

import { DecisionEngineQueries }                   from '@src/queries';
import { convertEmptyStringToNull, runIfFunction } from '@src/util';
import { queriesHaveData, queriesLoading }         from '@src/store/_apolloUtil';
import { StyledContainer }                         from '@src/views/containers';
import { QueryDialogProps }                        from '@src/views/components/from-query/QueryOpDialog';
import { ErrorBoundary }                           from '@src/views/components/util/errorHandling/ErrorBoundary';
import { ErrorService }                            from '@src/services/ErrorService';
import { ProjectQueries }                          from "@src/queries/ProjectQueries";


export type ProjectActivityBulkUpdateShellFormContainerProps =
    PropsWithChildren<any> &
    {
        operationDisabled: QueryDialogProps['operationDisabled'],
        mutation: QueryDialogProps['mutation'],
        graphArgs: QueryDialogProps['graphArgs'],
        onComplete: QueryDialogProps['onComplete'],
        onError: QueryDialogProps['onError'],
        onClose: QueryDialogProps['onClose'],
        onSubmit: QueryDialogProps['onSubmit']
        isProcessing: boolean,
        isLoading: boolean,
        formikRef: FormikProps<any>,
    }

export interface ProjectActivityBulkUpdateShellProps<T> extends PropsWithChildren<any> {
    ids: Uuid,
    projectId: Uuid,
    projectVersionId?: Uuid,
    closeOnEntityUpdate?: boolean,

    onUpdateComplete?(
        updatedAttributes: Partial<T>,
        projectActivitySet: CorvalProjectActivity[],
    ): void,

    onUpdateError?( err: any ): void,

    onUpdateCanceled?( e: SystemEvent ): void,

    formContainer: FC<ProjectActivityBulkUpdateShellFormContainerProps>,
    defaultFormValues?: T,
    validationSchema?: Yup.ObjectSchema,

    prepareFormValuesForMutation?( formValues: T, formik: FormikProps<any> ): any,

    supportToasts?: boolean,
    supportModals?: boolean,
}

type ProjectActivityBulkUpdateDialogFormConfig<T> = FormikConfig<ProjectActivityBulkUpdateShellProps<T>['defaultFormValues']>

const ProjectActivityBulkUpdateShellDefaultProps = {
    ids:                 null,
    projectId:           null,
    projectVersionId:    null,
    onUpdateComplete:    null,
    onUpdateError:       null,
    closeOnEntityUpdate: false,

    formContainer:     React.Fragment,
    defaultFormValues: {},
    validationSchema:  null,
} satisfies ProjectActivityBulkUpdateShellProps<any>

function ProjectActivityBulkUpdateShell<T>( { children, ...p }: ProjectActivityBulkUpdateShellProps<T> ){

    const props = { ...ProjectActivityBulkUpdateShellDefaultProps, ...p };

    let { projectId, projectVersionId, defaultFormValues } = props,
        [ isLoading, setIsLoading ]                        = useState( false ),
        [ isProcessing, setIsProcessing ]                  = useState( false ),
        [ isReadyToClose, setIsReadyToClose ]              = useState( false ),
        [ projectActivitySet, setProjectActivitySet ]      = useState<CorvalProjectActivity[]>( null ),
        { getUpdateProjectActivityInputData }              = useProjectActivities(),
        { addStringIsDateFormatTest }                      = useYupExtensions(),
        toastActions                                       = useSystemToasts(),
        modalActions                                       = useSystemModals(),
        formikRef                                          = useRef<FormikProps<ProjectActivityBulkUpdateShellProps<T>['defaultFormValues']>>( {} as any );

    const isCorvalManaged = _.isEmpty( projectVersionId );

    /* Get Current Project Activity Set Request */
    const getProjectActivityReqFragment = isCorvalManaged
                                          ? `${ DecisionEngineQueries.fragments.PROJECT_ACTIVITY.USER_EDIT_FIELDS }`
                                          : `${ DecisionEngineQueries.fragments.STATIC_PROJECT_ACTIVITY.USER_EDIT_FIELDS( projectId ) }`;

    const getProjectActivityReqQuery = DecisionEngineQueries.queries.GET_PROJECT_ACTIVITIES( getProjectActivityReqFragment, projectVersionId )

    const getProjectActivitySetReq = useQuery( getProjectActivityReqQuery, {
        variables:   {
            projectId,
            projectVersionId,
            where: { id: { in: props.ids } }
        },
        fetchPolicy: 'network-only'
    } );

    let dataIsLoading = queriesLoading( getProjectActivitySetReq ) || _.isNull( projectActivitySet );

    useEffect( () => {

        if( isReadyToClose && props.closeOnEntityUpdate && props.supportModals ){

            modalActions.toggle( false );

        } else {

            setIsReadyToClose( false );
        }

    }, [ projectActivitySet ] )

    useEffect( () => {

        if( !queriesLoading( getProjectActivitySetReq ) &&
            queriesHaveData( getProjectActivitySetReq ) && !isProcessing ){

            let projectActivitySet = ( isCorvalManaged
                                       ? getProjectActivitySetReq.data.projectActivites
                                       : getProjectActivitySetReq.data.staticProjectActivites
                                     ) ?? null;

            setProjectActivitySet( projectActivitySet );

            // Set formik values
            formikRef.current.resetForm( {
                values: defaultFormValues as T
            } );
        }

    }, [ getProjectActivitySetReq ] );

    let getUpdatedDataObject = () => {

        let inputData = {};

        // Collect only the form values that were changed, and that apply to the model
        _.mapObject( formikRef.current.values as any, ( value, key ) => {

            if( formikRef.current.initialValues[ key ] !== value ){

                inputData[ key ] = convertEmptyStringToNull( value );
            }

        } );

        return inputData;
    }

    let getGraphArgs = () => {

        if( !projectActivitySet || !( formikRef.current?.values ) ) return {};

        const newProjectActivitySetData = projectActivitySet.map( ( projectActivity: CorvalProjectActivity ) => {

            // get all mutated values
            let mutatedData = props.prepareFormValuesForMutation(
                formikRef.current?.values,
                formikRef.current
            );

            // get required model data for mutation to occur
            let mandatoryData = isCorvalManaged
                                ? getUpdateProjectActivityInputData( projectActivity )
                                : { id: projectActivity.id };

            return {
                ...mandatoryData,
                ...mutatedData,
            }
        } );

        const projectActivityGraphArgs = {
            variables: {
                input: {
                    projectId:         projectId,
                    projectActivities: newProjectActivitySetData
                }
            }
        }

        const staticProjectActivityGraphArgs = {
            variables: {
                input: {
                    projectVersionId: projectVersionId,
                    activityChanges:  {
                        modified: newProjectActivitySetData,
                    }
                }
            }
        }

        return isCorvalManaged
               ? projectActivityGraphArgs
               : staticProjectActivityGraphArgs;
    }

    const handleOnRequestSubmit: QueryDialogProps['onSubmit'] = () => {

        setIsLoading( true );
        setIsProcessing( true );
        setIsReadyToClose( false );
    }

    const handleOnRequestComplete: QueryDialogProps['onComplete'] = async ( res ) => {

        if( res ){

            if( props.supportToasts ){
                toastActions.success( 'Activity Updated!' );
            }

            setIsLoading( false );

            // Reset the form itself.
            formikRef.current.resetForm( { values: formikRef.current.values } );

            setIsReadyToClose( true );

            // Refetch the set, updating cache models
            getProjectActivitySetReq.refetch().then( res => {

                let projectActivitySet = res.data.projectActivites ?? null;

                setProjectActivitySet( projectActivitySet );

                // Run post update method w/ delta objects
                runIfFunction(
                    props.onUpdateComplete,
                    getUpdatedDataObject(),
                    projectActivitySet,
                );

                setIsProcessing( false );

            } );

        } else {

            setIsProcessing( false );
        }
    }

    const handleOnRequestError: QueryDialogProps['onError'] = async ( error: Error ) => {

        setIsLoading( false );
        setIsReadyToClose( false );

        if( props.supportToasts ){
            toastActions.error( 'Activity Set Not Updated!', ErrorService.getGQLErrorMessage( error ) ?? 'Something prevented Corval from updating the activity set. Please try again.' );
        }

        runIfFunction( props.onUpdateError, formikRef.current.values );
    }

    const handleOnRequestCancelled: QueryDialogProps['onClose'] = ( e ) => {

        setIsLoading( false );
        setIsReadyToClose( false );

        runIfFunction( props.onUpdateCanceled, e );
    }

    const getMappedRequestData = (): ProjectActivityBulkUpdateShellProps<T>['defaultFormValues'] => {

        return props.defaultFormValues as T;
    }

    // add display date validation
    addStringIsDateFormatTest()

    let formProps: ProjectActivityBulkUpdateDialogFormConfig<T> = {
        innerRef:           formikRef,
        isInitialValid:     false,
        enableReinitialize: true,
        validateOnChange:   true,
        initialValues:      getMappedRequestData(),
        validationSchema:   props.validationSchema ?? Yup.object().shape( {} ),
        onSubmit:           null,
    };

    const FormContainer = props.formContainer ?? StyledContainer;

    return (

        <Formik { ...formProps }>

            { formik => {

                return (

                    <FormContainer
                        formikRef={ formikRef }
                        isProcessing={ isProcessing }
                        isLoading={ isLoading }
                        operationDisabled={ isLoading || !formik.isValid || !formik.dirty }
                        mutation={ isCorvalManaged
                                   ? DecisionEngineQueries.mutations.UPDATE_PROJECT_ACTIVITIES( getProjectActivityReqFragment )
                                   : ProjectQueries.mutations.UPDATE_PROJECT_VERSION_DATA() }
                        graphArgs={ getGraphArgs }
                        onSubmit={ handleOnRequestSubmit }
                        onComplete={ handleOnRequestComplete }
                        onError={ handleOnRequestError }
                        onClose={ handleOnRequestCancelled }>

                        <ErrorBoundary>

                            <Form>

                                { _.isFunction( children )
                                  ? children( { formik, isProcessing, isLoading } )
                                  : children }

                            </Form>

                        </ErrorBoundary>

                    </FormContainer>
                )
            } }
        </Formik>
    );
};

export { ProjectActivityBulkUpdateShell }
